import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  TextField,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import {
  VideoLibrary as VideoIcon,
  FitnessCenter as FitnessCenterIcon,
  CheckCircle as CheckIcon,
  Save as SaveIcon,
  RotateLeft as UndoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import moment from "moment";
import StandardButton from "../../../../../components/StandardButton";

const VideoViewerDialog = ({
  open,
  onClose,
  video,
  onProcessVideo,
  thumbnail,
}) => {
  const [feedback, setFeedback] = useState("");

  // Reset feedback when dialog opens with a new video
  useEffect(() => {
    if (video) {
      setFeedback(video.feedback || "");
    }
  }, [video]);

  if (!video) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "auto",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ position: "relative", p: 1 }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 2,
          pb: 1,
          overflow: "visible",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          {/* Video player - dynamically sized based on content */}
          <Box
            sx={{
              textAlign: "center",
              bgcolor: "black",
              borderRadius: 1,
              position: "relative",
              height: "auto",
              maxHeight: "45vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <video
              controls
              autoPlay
              poster={thumbnail}
              style={{
                width: "100%",
                maxHeight: "45vh",
                objectFit: "contain",
              }}
              src={video.url}
            >
              Your browser does not support the video tag.
            </video>
          </Box>

          {/* Title and action buttons */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="medium">
              {video.metadata?.exercise
                ? video.metadata.exercise.exerciseName || "Exercise Video"
                : "Shared Video"}
            </Typography>

            <Stack direction="row" spacing={1}>
              <StandardButton
                size="small"
                variant={video.processed ? "secondary" : "primary"}
                startIcon={video.processed ? <UndoIcon /> : <CheckIcon />}
                onClick={() => onProcessVideo(video, feedback)}
              >
                {video.processed ? "Unprocess" : "Mark Processed"}
              </StandardButton>
            </Stack>
          </Stack>

          {/* Status information */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              icon={
                video.metadata?.exercise ? <FitnessCenterIcon /> : <VideoIcon />
              }
              label={
                video.metadata?.exercise ? "Exercise Video" : "General Video"
              }
              color={video.metadata?.exercise ? "primary" : "secondary"}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              Shared {moment(video.createdOn.toDate()).fromNow()}
            </Typography>
            {video.processed && video.processedBy && (
              <Typography variant="body2" color="success.main">
                Processed by {video.processedBy.firstName}{" "}
                {video.processedBy.lastName}
              </Typography>
            )}
          </Stack>

          {/* Exercise details if available */}
          {video.metadata?.exercise && (
            <Paper
              sx={{ p: 2, bgcolor: "rgba(25, 118, 210, 0.05)" }}
              variant="outlined"
            >
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Exercise Details
              </Typography>
              <Typography variant="body2">
                {video.metadata?.exercise.exerciseName && (
                  <>
                    <strong>Exercise:</strong>{" "}
                    {video.metadata.exercise.exerciseName}
                    <br />
                  </>
                )}
                {video.metadata?.exercise.sets && (
                  <>
                    <strong>Sets:</strong> {video.metadata.exercise.sets}
                    <br />
                  </>
                )}
                {video.metadata?.exercise.reps && (
                  <>
                    <strong>Reps:</strong> {video.metadata.exercise.reps}
                    <br />
                  </>
                )}
                {/* Always show notes if available, whether it's from exercise metadata or video itself */}
                <strong>Notes:</strong>{" "}
                {video.metadata?.notes || video.notes || "No notes available"}
              </Typography>
            </Paper>
          )}

          {/* Coach notes/feedback - always show this field */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Your Feedback
            </Typography>
            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Add your feedback about this video..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              variant="outlined"
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 2 }}>
        <StandardButton variant="secondary" onClick={onClose}>Cancel</StandardButton>
        <StandardButton
          variant="primary"
          startIcon={<SaveIcon />}
          onClick={() => onProcessVideo(video, feedback)}
        >
          {video.processed ? "Update Feedback" : "Process & Save Feedback"}
        </StandardButton>
      </DialogActions>
    </Dialog>
  );
};

export default VideoViewerDialog;
